// pages/TeacherLogin.jsx
import { useState } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  Container,
  Grid,
  InputAdornment,
  Fade
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import SchoolIcon from "@mui/icons-material/School";
import LoginIcon from "@mui/icons-material/Login";

const TeacherLogin = () => {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const handleLogin = async () => {
    try {
      setError("");
      setLoading(true);

      if (!code.trim()) {
        setError("School code is required");
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API_BASE_URL}/school-login`, { schoolCode: code });
      if (response?.data?.status === "SUCCESS") {
        await localStorage.setItem("schoolId", response.data.formId);
        await localStorage.setItem("schoolTemplate", JSON.stringify(response.data.templateData));
        await localStorage.setItem("schoolName", JSON.stringify(response.data.templateData?.formName??"Teacher Dashboard"));
        navigate("/teacher/dashboard");
        Swal.fire({ text: response?.data?.message, icon: "success" });
      } else {
        Swal.fire({ text: response?.data?.message, icon: "warning" });
      }
    } catch (error) {
      Swal.fire({ text: error?.message, icon: "warning" });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #8161a0 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Fade in={true} timeout={1000}>
          <Paper
            elevation={12}
            sx={{
              p: 4,
              borderRadius: 3,
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "primary.main",
                  width: 70,
                  height: 70,
                  mb: 2,
                  boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                }}
              >
                <SchoolIcon sx={{ fontSize: 35 }} />
              </Avatar>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  textAlign: "center",
                  mb: 1,
                }}
              >
                Teacher Login
              </Typography>
              {/* <Typography
                variant="body1"
                color="text.secondary"
                sx={{ textAlign: "center", mb: 3 }}
              >
                Enter your school code to access the dashboard
              </Typography> */}
            </Box>

            <Box component="form" noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="School Code"
                    variant="outlined"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onKeyPress={handleKeyPress}
                    error={!!error}
                    helperText={error}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SchoolIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleLogin}
                disabled={loading}
                startIcon={!loading && <LoginIcon />}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                  boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                  "&:hover": {
                    background: "linear-gradient(45deg, #1976D2 30%, #00BCD4 90%)",
                    // transform: "translateY(-2px)",
                    boxShadow: "0 6px 10px 2px rgba(33, 203, 243, .3)",
                  },
                  transition: "all 0.3s ease-in-out",
                }}
              >
                {loading ? "Logging in..." : "Access Dashboard"}
              </Button>

              <Box sx={{ textAlign: "center", mt: 2 }}>
                {/* <Typography variant="body2" color="text.secondary">
                  Need help? Contact your school administrator
                </Typography> */}
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default TeacherLogin;
